import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Cloud, 
  Users, 
  FileText, 
  DollarSign, 
  TrendingUp, 
  Network,
  Shield,
  Zap,
  Database,
  BarChart3,
  GitBranch,
  Lock,
  CheckCircle2,
  ArrowRight,
  Layers,
  Activity,
  Target,
  Workflow,
  Settings,
  Briefcase,
  ShoppingCart,
  Scale,
  Cpu,
  Server,
  Building2,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import EarlyAccessModal from '../components/EarlyAccessModal';

const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scrollToArchitecture = () => {
    document.getElementById('architecture')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900">SLOP+</span>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-slate-600 hover:text-blue-600 transition-colors font-medium">Features</a>
            <a href="#architecture" className="text-slate-600 hover:text-blue-600 transition-colors font-medium">Architecture</a>
            <a href="#roadmap" className="text-slate-600 hover:text-blue-600 transition-colors font-medium">Roadmap</a>
            <Button onClick={() => setIsModalOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
              Request Access
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 opacity-95"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-400/30 rounded-full px-4 py-2 mb-8">
            <Zap className="w-4 h-4 text-blue-400" />
            <span className="text-blue-200 text-sm font-medium">The AI Cost Control Plane</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            The End-to-End AI Cost
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Control Plane</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            SLOP+ unifies SaaS, cloud, contracts, infra, workforce, and business systems into one intelligent operating system that understands your costs — and autonomously optimizes them.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Button 
              onClick={() => setIsModalOpen(true)} 
              size="lg" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/50 hover:shadow-xl hover:shadow-blue-500/60 transition-all duration-300"
            >
              Request Early Access
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              onClick={scrollToArchitecture}
              size="lg" 
              variant="outline" 
              className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold backdrop-blur-sm transition-all duration-300"
            >
              Explore the Architecture
            </Button>
          </div>
          
          {/* Control Plane Visual */}
          <div className="mt-16 relative">
            <div className="bg-slate-800/50 backdrop-blur-md border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-6 mb-8">
                {[
                  { icon: Cloud, label: 'Cloud' },
                  { icon: Users, label: 'HR' },
                  { icon: FileText, label: 'Contracts' },
                  { icon: DollarSign, label: 'Finance' },
                  { icon: Briefcase, label: 'Sales' },
                  { icon: Server, label: 'Infra' }
                ].map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center space-y-2 group">
                    <div className="w-16 h-16 bg-blue-500/10 border border-blue-400/30 rounded-xl flex items-center justify-center group-hover:bg-blue-500/20 transition-all duration-300">
                      <item.icon className="w-8 h-8 text-blue-400" />
                    </div>
                    <span className="text-xs text-slate-400">{item.label}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center justify-center mb-8">
                <div className="flex items-center space-x-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{animationDelay: `${i * 0.2}s`}}></div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6">
                <div className="flex items-center justify-center space-x-3">
                  <Cpu className="w-8 h-8 text-white" />
                  <span className="text-xl font-bold text-white">AI Control Plane</span>
                </div>
                <p className="text-center text-blue-100 mt-2 text-sm">Unified Intelligence · Autonomous Optimization</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Enterprise cost is fragmented across
              <br />
              <span className="text-blue-600">the entire organization</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Network, title: 'SaaS Sprawl', desc: 'Unused licenses & shadow IT' },
              { icon: Cloud, title: 'Cloud Overage', desc: 'Unpredictable billing spikes' },
              { icon: FileText, title: 'Hidden Contracts', desc: 'Unknown obligations & terms' },
              { icon: Settings, title: 'Orphaned Tools', desc: 'No ownership or accountability' },
              { icon: Users, title: 'Workforce Drift', desc: 'Access cost misalignment' },
              { icon: ShoppingCart, title: 'Duplicate Vendors', desc: 'Multiple teams, same service' },
              { icon: BarChart3, title: 'Usage Disconnect', desc: 'Poor usage-to-spend correlation' },
              { icon: Activity, title: 'Zero Visibility', desc: 'No cross-department insight' }
            ].map((item, idx) => (
              <Card key={idx} className="p-6 hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-blue-300 group">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-200 transition-colors">
                  <item.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-xl text-slate-700 font-medium">
              <span className="text-blue-600 font-bold">SLOP+</span> brings everything together into one unified cost control plane.
            </p>
          </div>
        </div>
      </section>

      {/* Value Prop Section */}
      <section id="features" className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              One Platform. <span className="text-blue-600">Total Cost Control.</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-blue-400 bg-white">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Network className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">LicenseOps AI</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Rightsize licenses</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Identify shelfware</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Discover shadow IT</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Map usage → cost → value</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-blue-400 bg-white">
              <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
                <FileText className="w-7 h-7 text-cyan-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">ContractOps AI</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Auto-ingest contracts</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Normalize terms & obligations</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Track renewals</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Predict contract impact on spend</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-blue-400 bg-white">
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6">
                <Cloud className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">CloudOps AI</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">AWS/GCP/Azure unit cost modeling</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Waste detection</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">AI-driven rightsizing</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Forecasting & anomaly detection</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-blue-400 bg-white">
              <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">FinOps AI</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">CFO-level cost views</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Policy-as-code budgets</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Scenario planning</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">Automated savings execution</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Connectors Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              SLOP+ connects to <span className="text-blue-600">every system</span> in your company
            </h2>
            <p className="text-xl text-slate-600">Not just IT — across the entire organization</p>
          </div>
          
          <div className="space-y-8">
            <Card className="p-6 border-slate-200 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Cpu className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">IT & ENGINEERING</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Okta', 'Azure AD', 'Google Workspace', 'Atlassian', 'ServiceNow', 'GitHub', 'Datadog', 'Splunk', 'Grafana', 'Zscaler', 'Cloudflare', 'Cisco', 'Kubernetes'].map((system, sIdx) => (
                  <span key={sIdx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default">
                    {system}
                  </span>
                ))}
              </div>
            </Card>

            <Card className="p-6 border-slate-200 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Cloud className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">CLOUD & INFRASTRUCTURE</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {['AWS', 'GCP', 'Azure', 'OCI', 'VMware', 'Databricks', 'Snowflake', 'Hyper-V', 'Storage', 'CDN', 'Networking'].map((system, sIdx) => (
                  <span key={sIdx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default">
                    {system}
                  </span>
                ))}
              </div>
            </Card>

            <Card className="p-6 border-slate-200 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">FINANCE & PROCUREMENT</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {['NetSuite', 'SAP', 'Oracle Fusion', 'Workday Financials', 'Coupa', 'Ariba', 'Tipalti', 'Vendor Payments'].map((system, sIdx) => (
                  <span key={sIdx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default">
                    {system}
                  </span>
                ))}
              </div>
            </Card>

            <Card className="p-6 border-slate-200 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-violet-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">HR & WORKFORCE</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Workday', 'BambooHR', 'Rippling', 'ADP', 'Gusto', 'UKG', 'Compensation Systems', 'Headcount Models'].map((system, sIdx) => (
                  <span key={sIdx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default">
                    {system}
                  </span>
                ))}
              </div>
            </Card>

            <Card className="p-6 border-slate-200 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">SALES & MARKETING</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Salesforce', 'HubSpot', 'Marketo', 'Outreach', 'Zendesk', 'Intercom', 'Google Ads', 'Meta Ads', 'LinkedIn Ads'].map((system, sIdx) => (
                  <span key={sIdx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default">
                    {system}
                  </span>
                ))}
              </div>
            </Card>

            <Card className="p-6 border-slate-200 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                  <Scale className="w-6 h-6 text-slate-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">LEGAL & CONTRACTS</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Ironclad', 'DocuSign', 'Box', 'Dropbox', 'Google Drive', 'PDF Extractors'].map((system, sIdx) => (
                  <span key={sIdx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default">
                    {system}
                  </span>
                ))}
              </div>
            </Card>

            <Card className="p-6 border-slate-200 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">SECURITY & COMPLIANCE</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {['IAM Systems', 'SIEMs', 'Compliance Platforms', 'Audit Logs', 'Identity Governance'].map((system, sIdx) => (
                  <span key={sIdx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default">
                    {system}
                  </span>
                ))}
              </div>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-xl text-slate-700 font-medium">
              One control plane to <span className="text-blue-600 font-bold">unify all organizational cost data</span>
            </p>
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section id="architecture" className="py-20 px-6 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              How the Cost Control Plane Works
            </h2>
            <p className="text-xl text-slate-300">
              Ingest → Normalize → Correlate → Predict → Optimize → Autonomize
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                step: '1',
                icon: Database,
                title: 'Connect',
                desc: 'Ingest SaaS, cloud, HR, finance, contracts, and infra data.'
              },
              {
                step: '2',
                icon: Layers,
                title: 'Normalize',
                desc: 'Apply the SLOP+ Canonical Cost Schema to unify all models.'
              },
              {
                step: '3',
                icon: GitBranch,
                title: 'Correlate',
                desc: 'Link usage → cost → contracts → workforce → systems → infra.'
              },
              {
                step: '4',
                icon: TrendingUp,
                title: 'Predict',
                desc: 'AI models forecast spend across vendors, teams, stores, workloads.'
              },
              {
                step: '5',
                icon: Target,
                title: 'Optimize',
                desc: 'AI agents recommend rightsizing, vendor consolidation, deprovisioning.'
              },
              {
                step: '6',
                icon: Zap,
                title: 'Autonomize',
                desc: 'Autonomous engine executes policy-based cost savings automatically.'
              }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <Card className="p-6 bg-slate-800/50 backdrop-blur-md border-slate-700/50 hover:bg-slate-800/70 transition-all duration-300 h-full">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500/20 border border-blue-400/30 rounded-full flex items-center justify-center font-bold text-blue-400">
                      {item.step}
                    </div>
                    <div className="w-12 h-12 bg-blue-500/10 border border-blue-400/30 rounded-xl flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{item.desc}</p>
                </Card>
                {idx < 5 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-blue-400/30 z-10"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Enterprise Features — <span className="text-blue-600">Built for Scale</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Shield,
                title: 'Security & Governance',
                desc: 'SOC2 model, RBAC, encryption, audit trails, zero-trust connectors.',
                features: ['End-to-end encryption', 'Role-based access control', 'Comprehensive audit logs', 'Zero-trust architecture']
              },
              {
                icon: Network,
                title: 'Unified Cost Graph',
                desc: 'Full correlation of spend across the entire organization.',
                features: ['Cross-system correlation', 'Real-time cost tracking', 'Dependency mapping', 'Impact analysis']
              },
              {
                icon: Workflow,
                title: 'AI Automation Engine',
                desc: 'Rightsizing, renewals, deprecations, workflow automation.',
                features: ['Automated rightsizing', 'Smart renewals', 'Policy enforcement', 'Workflow orchestration']
              },
              {
                icon: Server,
                title: 'Scalable Multi-Cloud Architecture',
                desc: 'Built for large enterprises, multi-region, multi-tenant.',
                features: ['Multi-region support', 'High availability', 'Enterprise SLAs', 'Global deployment']
              }
            ].map((item, idx) => (
              <Card key={idx} className="p-8 hover:shadow-2xl transition-all duration-300 border-slate-200 hover:border-blue-400">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 mb-6">{item.desc}</p>
                <ul className="space-y-2">
                  {item.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-center space-x-2 text-sm text-slate-700">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section id="roadmap" className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              The Evolution of the <span className="text-blue-600">Control Plane</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 border-2 border-blue-200 hover:shadow-xl transition-all duration-300 bg-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
              <div className="relative">
                <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold mb-2">
                  Now
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-6">MVP</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Core connectors</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Canonical cost schema</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">SaaS discovery</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Initial CloudOps insights</span>
                  </li>
                </ul>
              </div>
            </Card>

            <Card className="p-8 border-2 border-cyan-200 hover:shadow-xl transition-all duration-300 bg-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
              <div className="relative">
                <div className="inline-block px-4 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-bold mb-2">
                  Q1–Q2
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-6">Enterprise</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">ContractOps</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">AI FinOps advisor</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Real-time unit cost engine</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Renewal Autopilot</span>
                  </li>
                </ul>
              </div>
            </Card>

            <Card className="p-8 border-2 border-emerald-200 hover:shadow-xl transition-all duration-300 bg-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
              <div className="relative">
                <div className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold mb-2">
                  Q3–Q4
                </div>
                <h3 className="text-3xl font-bold text-slate-900 mb-6">Autonomous</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">Zero-click cost optimization</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">AI governance</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700">CFO autopilot mode</span>
                  </li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 via-cyan-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Bring your entire organization onto
            <br />
            the AI Cost Control Plane
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Join leading enterprises in autonomously optimizing costs across every system
          </p>
          <Button 
            onClick={() => setIsModalOpen(true)}
            size="lg" 
            className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300"
          >
            Request Early Access
            <ArrowRight className="ml-2 w-6 h-6" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">SLOP+</span>
              </div>
              <p className="text-sm">The AI Cost Control Plane for the entire organization.</p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-blue-400 transition-colors">Features</a></li>
                <li><a href="#architecture" className="hover:text-blue-400 transition-colors">Architecture</a></li>
                <li><a href="#roadmap" className="hover:text-blue-400 transition-colors">Roadmap</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Security</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-blue-400 transition-colors">About</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-blue-400 transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm">© 2025 SLOP+. All rights reserved.</p>
            <p className="text-sm mt-4 md:mt-0">sloplus.ai</p>
          </div>
        </div>
      </footer>

      <EarlyAccessModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default Home;